import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Play, ArrowLeft, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/lib/api";
import { NotFound } from "@/pages/SimplePages";

export default function ProjectPage() {
  const { slug } = useParams();
  const { data } = useContent();
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const s = data?.settings || {};
  const projects = data?.projects || [];
  const idx = projects.findIndex((p) => p.slug === slug);
  const project = idx >= 0 ? projects[idx] : null;

  if (!data) return null;
  if (!project) return <NotFound />;

  const service = (data.services || []).find((x) => x.slug === project.service);
  const prev = projects[(idx - 1 + projects.length) % projects.length];
  const next = projects[(idx + 1) % projects.length];
  const poster = project.poster || `/posters/projet-${project.slug}.svg`;
  const vids = project.videos && project.videos.length
    ? project.videos
    : project.videoUrl
      ? [{ titre: project.titre, url: project.videoUrl, format: "paysage" }]
      : [];
  const current = vids[active] || vids[0];
  const vertical = current?.format === "portrait";

  return (
    <main data-testid={`project-page-${project.slug}`}>
      <Seo
        title={`${project.titre} — ${project.categorie} | SLIZ`}
        description={project.resume.slice(0, 150)}
        path={`/realisations/${project.slug}/`}
      />
      <div className="wrap" style={{ paddingTop: 120 }}>
        <p className="t-label" style={{ marginBottom: 20 }} data-testid="project-meta-top">
          <Link to="/realisations/" style={{ color: "var(--ink3)" }}>Réalisations</Link> — {project.categorie}
        </p>
        <h1 className="t-h1" style={{ margin: 0, fontSize: "clamp(34px,5vw,72px)" }}>{project.titre}</h1>
        <p className="t-chapo" style={{ marginTop: 20 }}>{project.client} · {project.annee} · {project.canal}</p>
      </div>

      <div className="wrap" style={{ marginTop: 48 }}>
        <div
          className="work-frame"
          style={vertical && playing
            ? { aspectRatio: "9 / 16", maxWidth: 420, margin: "0 auto" }
            : { aspectRatio: "16 / 9" }}
          data-testid="project-video-frame"
        >
          {playing && current ? (
            current.url.includes("youtube") || current.url.includes("vimeo") ? (
              <iframe src={current.url} title={current.titre} style={{ width: "100%", height: "100%", border: 0 }} allow="autoplay; fullscreen" allowFullScreen data-testid="project-player" />
            ) : (
              <video key={current.url} src={current.url} controls autoPlay playsInline style={{ width: "100%", height: "100%", background: "#000" }} data-testid="project-player" />
            )
          ) : (
            <>
              <img src={poster} alt={`${project.titre} — film ${project.categorie} pour ${project.client}, produit par SLIZ`} width="1600" height="900" fetchPriority="high" />
              {current && (
                <button
                  onClick={() => setPlaying(true)}
                  aria-label={`Lire ${project.titre}`}
                  data-testid="project-play"
                  style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(3,6,12,0.25)", border: "none", cursor: "pointer" }}
                >
                  <span style={{ width: 72, height: 72, border: "1px solid rgba(255,255,255,0.85)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                    <Play size={24} strokeWidth={1.5} />
                  </span>
                </button>
              )}
            </>
          )}
        </div>
        {vids.length > 1 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 24 }} data-testid="project-gallery">
            {vids.map((v, i) => (
              <button
                key={v.url}
                onClick={() => { setActive(i); setPlaying(true); }}
                className={`works-filter ${playing && i === active ? "active" : ""}`}
                data-testid={`project-video-${i}`}
              >
                <Play size={11} strokeWidth={1.5} style={{ marginRight: 8, verticalAlign: "-1px" }} />
                {v.titre}{v.format === "portrait" ? " · 9:16" : ""}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="wrap" style={{ paddingTop: 72, paddingBottom: 80 }}>
        <div className="grid gap-12 lg:grid-cols-[220px_1fr]">
          <div>
            <p className="t-label" style={{ marginBottom: 16 }}>Rôle de SLIZ</p>
            <p className="t-body" style={{ margin: 0, fontSize: 14 }} data-testid="project-role">{project.role}</p>
            <p className="t-label" style={{ margin: "32px 0 16px" }}>Diffusion</p>
            <p className="t-body" style={{ margin: 0, fontSize: 14 }}>{project.canal}</p>
          </div>
          <Reveal>
            <div style={{ maxWidth: 760 }}>
              <p className="t-chapo" style={{ marginTop: 0, color: "var(--ink)" }} data-testid="project-resume">{project.resume}</p>
              {service && (
                <Link to={`/${service.slug}/`} className="link-arrow" style={{ marginTop: 28 }} data-testid="project-service-link">
                  {service.h1} <ArrowRight size={13} strokeWidth={1.5} />
                </Link>
              )}
            </div>
          </Reveal>
        </div>

        <div style={{ borderTop: "1px solid var(--li)", marginTop: 80, paddingTop: 32, display: "flex", justifyContent: "space-between", gap: 20 }}>
          <Link to={`/realisations/${prev.slug}/`} className="link-arrow" data-testid="project-prev"><ArrowLeft size={13} strokeWidth={1.5} /> {prev.titre}</Link>
          <Link to={`/realisations/${next.slug}/`} className="link-arrow" data-testid="project-next">{next.titre} <ArrowRight size={13} strokeWidth={1.5} /></Link>
        </div>
      </div>
    </main>
  );
}
